/**
 * The account a signature's access is for, as the portal names it.
 *
 * The signed payload carries only the identity's id. The request the person
 * came from names it: a requester's own request, or the approval an approver
 * opened (its assignment rides in router state). Read only when the payload
 * names an account; until it arrives, or when it cannot be read, the page
 * shows the id. `accountLabel` uses it only for that same identity.
 */
import { MF_ACCESS } from "state/members/access_v2/catalogs";
import type { StepUpChallenge } from "state/members/stepUp";

import type { KnownAccount } from "./payloadProse";

// A detail of the page, so a read that fails must not suspend or blank it.
const QUIET = { suspense: false, useErrorBoundary: false };

export const useKnownAccount = (
  challenge: StepUpChallenge | undefined,
  actionAssigneeId: string | null,
): KnownAccount | null => {
  const named = Boolean(challenge?.payload.entitlement?.identityId);
  const own = named && challenge?.purpose === "request_submission";
  const approving = named && challenge?.purpose === "approval";

  const request = MF_ACCESS.useGetAccessRequest(
    own ? challenge?.requestId : null,
    QUIET,
  );
  const approval = MF_ACCESS.useGetApprovalRequest(
    approving ? actionAssigneeId : null,
    QUIET,
  );

  const source = own ? request : approving ? approval : undefined;
  // The assignment in router state could name another request.
  if (!source || source.id !== challenge?.requestId) return null;
  return source.requestedIdentity ?? null;
};
